/**
 * HorizontalRail — snap-scrolling horizontal list used by every "rail" on the
 * home page (categories / deals / buy again / recommended / coupons / etc).
 *
 *   <HorizontalRail>
 *     {items.map((it) => <Card key={it.id}>…</Card>)}
 *   </HorizontalRail>
 *
 * Renders a desktop grid when there's room, a swipable snap-scroll on mobile.
 */
import React from 'react';
import { css } from './tokens';

export interface HorizontalRailProps {
  children: React.ReactNode;
  /** Minimum card width on mobile snap-scroll. */
  itemMinWidth?: number;
  gap?: number;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

export const HorizontalRail: React.FC<HorizontalRailProps> = ({
  children,
  itemMinWidth = 152,
  gap = 12,
  className,
  style,
  ariaLabel,
}) => (
  <div
    role="region"
    aria-label={ariaLabel}
    className={['pm-h-rail', className].filter(Boolean).join(' ')}
    style={{
      display: 'flex',
      gap,
      overflowX: 'auto',
      overflowY: 'visible',
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
      paddingBottom: 4,
      margin: '0 -4px',
      ...style,
    }}
  >
    {React.Children.map(children, (child, i) => (
      <div
        key={i}
        style={{
          flexShrink: 0,
          minWidth: itemMinWidth,
          scrollSnapAlign: 'start',
          paddingLeft: i === 0 ? 4 : 0,
          paddingRight: 4,
        }}
      >
        {child}
      </div>
    ))}
    <style>{`
      .pm-h-rail::-webkit-scrollbar { height: 6px; }
      .pm-h-rail::-webkit-scrollbar-thumb { background: ${css.color.border}; border-radius: 999px; }
      .pm-h-rail::-webkit-scrollbar-track { background: transparent; }
    `}</style>
  </div>
);

export default HorizontalRail;

